import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Phone, Play, Pause, RotateCcw, Trash2, Settings, Sparkles,
  Send, MessageSquare, CheckCheck, Check, Eye, Users, Clock, AlertTriangle,
  RefreshCw, ExternalLink, FileText, Ban
} from "lucide-react";
import { toast } from "sonner";
import { errMsg, dateShort, dateTime } from "@/lib/format";
import WhatsAppBroadcastModal from "@/components/WhatsAppBroadcastModal";

const STATUSES = ["New", "Contacted", "Follow Up", "Interested", "Visit Scheduled",
  "Quotation Sent", "Negotiation", "Won", "Lost"];
const SOURCES = ["Website", "WhatsApp", "Instagram", "Facebook", "Walk-In", "Referral", "Google Ads"];

const statusStyle = (s) => ({
  draft: "bg-slate-100 text-slate-700",
  running: "bg-emerald-100 text-emerald-800",
  paused: "bg-indigo-100 text-indigo-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-rose-100 text-rose-800",
}[s] || "bg-slate-100 text-slate-700");

export default function Campaigns() {
  const [activeTab, setActiveTab] = useState("whatsapp");

  // Voice Campaigns state
  const [voiceItems, setVoiceItems] = useState([]);
  const [voiceLoading, setVoiceLoading] = useState(true);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [provider, setProvider] = useState("mock");

  // WhatsApp Broadcasts state
  const [broadcastItems, setBroadcastItems] = useState([]);
  const [broadcastSummary, setBroadcastSummary] = useState({
    total_broadcasts: 0,
    total_sent: 0,
    total_delivered: 0,
    total_read: 0,
  });
  const [broadcastLoading, setBroadcastLoading] = useState(true);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);

  // Recipient logs drawer/modal state
  const [viewBroadcastId, setViewBroadcastId] = useState(null);
  const [broadcastDetail, setBroadcastDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  function loadVoice() {
    setVoiceLoading(true);
    api.get("/campaigns").then(r => setVoiceItems(r.data.items))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setVoiceLoading(false));
    api.get("/settings/calling/provider").then(r => setProvider(r.data.provider)).catch(() => {});
  }

  function loadBroadcasts() {
    setBroadcastLoading(true);
    api.get("/whatsapp/broadcasts")
      .then(r => {
        setBroadcastItems(r.data.items || []);
        if (r.data.summary) setBroadcastSummary(r.data.summary);
      })
      .catch(e => toast.error(errMsg(e, "Failed to load WhatsApp broadcasts")))
      .finally(() => setBroadcastLoading(false));
  }

  useEffect(() => {
    loadVoice();
    loadBroadcasts();
  }, []);

  // Poll running broadcasts for live progress bar
  useEffect(() => {
    const hasRunning = broadcastItems.some(b => b.status === "running");
    if (!hasRunning) return;

    const interval = setInterval(() => {
      api.get("/whatsapp/broadcasts").then(r => {
        setBroadcastItems(r.data.items || []);
        if (r.data.summary) setBroadcastSummary(r.data.summary);
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(interval);
  }, [broadcastItems]);

  async function openBroadcastDetails(id) {
    setViewBroadcastId(id);
    setDetailLoading(true);
    try {
      const res = await api.get(`/whatsapp/broadcasts/${id}`);
      setBroadcastDetail(res.data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load broadcast recipients"));
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleCancelBroadcast(id) {
    if (!window.confirm("Are you sure you want to cancel this running broadcast? Remaining queued messages will not be sent.")) return;
    try {
      await api.post(`/whatsapp/broadcasts/${id}/cancel`);
      toast.success("Broadcast cancelled.");
      loadBroadcasts();
    } catch (e) {
      toast.error(errMsg(e, "Failed to cancel broadcast"));
    }
  }

  async function handleDeleteBroadcast(id) {
    if (!window.confirm("Are you sure you want to delete this broadcast record?")) return;
    try {
      await api.delete(`/whatsapp/broadcasts/${id}`);
      toast.success("Broadcast deleted.");
      loadBroadcasts();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete broadcast"));
    }
  }

  return (
    <div data-testid="campaigns-page" className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-emerald-600" /> Outbound Campaign Engine
          </h1>
          <p className="text-sm text-slate-600">
            Scale revenue with bulk personalized WhatsApp broadcasts & autonomous AI voice dialers
          </p>
        </div>
      </div>

      {/* Tabs Header */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger
              value="whatsapp"
              className="gap-2 text-xs font-semibold rounded-lg data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-xs"
            >
              <Send className="w-3.5 h-3.5" />
              <span>WhatsApp Broadcasts</span>
              {broadcastItems.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-emerald-100 text-emerald-800">
                  {broadcastItems.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="voice"
              className="gap-2 text-xs font-semibold rounded-lg data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-xs"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>AI Voice Calling</span>
              {voiceItems.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-indigo-100 text-indigo-800">
                  {voiceItems.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Action buttons based on active tab */}
          {activeTab === "whatsapp" ? (
            <Button
              onClick={() => setBroadcastModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 text-xs h-9 px-4 rounded-xl shadow-xs font-semibold"
            >
              <Plus className="w-4 h-4" />
              <span>New WhatsApp Broadcast</span>
            </Button>
          ) : (
            <div className="flex gap-2">
              <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="campaigns-settings-btn" variant="outline" size="sm" className="rounded-xl text-xs h-9">
                    <Settings className="h-4 w-4 mr-1.5" /> Calling Settings
                  </Button>
                </DialogTrigger>
                <CallingSettingsDialog onClose={() => setSettingsOpen(false)} />
              </Dialog>
              <Dialog open={voiceOpen} onOpenChange={setVoiceOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="campaigns-new-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white text-xs h-9 rounded-xl font-semibold">
                    <Plus className="h-4 w-4 mr-1.5" /> New Voice Campaign
                  </Button>
                </DialogTrigger>
                <NewCampaignDialog onSaved={() => { setVoiceOpen(false); loadVoice(); }} />
              </Dialog>
            </div>
          )}
        </div>

        {/* ── WHATSAPP BROADCAST TAB CONTENT ── */}
        <TabsContent value="whatsapp" className="space-y-5">
          {/* Summary Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 rounded-2xl border-slate-200/80 bg-white shadow-2xs">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>Total Broadcasts</span>
                <Send className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-2xl font-bold font-serif text-slate-900">
                {broadcastSummary.total_broadcasts}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Multi-channel campaigns</div>
            </Card>

            <Card className="p-4 rounded-2xl border-slate-200/80 bg-white shadow-2xs">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>Total Delivered</span>
                <CheckCheck className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-2xl font-bold font-serif text-slate-900">
                {broadcastSummary.total_sent}
              </div>
              <div className="text-[11px] text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                <span>Meta Cloud API Outbound</span>
              </div>
            </Card>

            <Card className="p-4 rounded-2xl border-slate-200/80 bg-white shadow-2xs">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>Delivery Rate</span>
                <Check className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-bold font-serif text-emerald-700">
                {broadcastSummary.total_sent > 0
                  ? `${Math.round(((broadcastSummary.total_delivered || broadcastSummary.total_sent) / broadcastSummary.total_sent) * 100)}%`
                  : "100%"}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Verified handoffs to device</div>
            </Card>

            <Card className="p-4 rounded-2xl border-slate-200/80 bg-white shadow-2xs">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>Read (Blue Ticks)</span>
                <Eye className="w-4 h-4 text-sky-500" />
              </div>
              <div className="text-2xl font-bold font-serif text-sky-700">
                {broadcastSummary.total_read}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Customer opened message</div>
            </Card>
          </div>

          {/* Broadcast Campaigns Table */}
          <Card className="border-slate-200/80 bg-white overflow-hidden rounded-2xl shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/80 text-slate-700 border-b border-slate-200/80">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Broadcast Campaign</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Progress (Sent/Total)</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Delivered</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Read (Ticks)</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Pacing Delay</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Created</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {broadcastLoading ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500 text-xs">
                        Loading WhatsApp broadcasts…
                      </td>
                    </tr>
                  ) : broadcastItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-12 text-center text-slate-500">
                        <div className="max-w-md mx-auto space-y-3">
                          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                            <Send className="w-6 h-6" />
                          </div>
                          <div className="text-sm font-semibold text-slate-800">No WhatsApp Broadcasts Yet</div>
                          <p className="text-xs text-slate-500">
                            Launch your first bulk promotional or retargeting campaign with dynamic variables and interactive CTA buttons.
                          </p>
                          <Button
                            onClick={() => setBroadcastModalOpen(true)}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs"
                          >
                            <Plus className="w-4 h-4 mr-1.5" /> Create Broadcast
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    broadcastItems.map((b) => {
                      const total = b.total_recipients || 0;
                      const sent = b.sent_count || 0;
                      const pct = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0;
                      const isRunning = b.status === "running";

                      return (
                        <tr key={b.id} className="hover:bg-slate-50/60 transition-colors">
                          {/* Name & Snippet */}
                          <td className="px-4 py-3.5">
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                              <span>{b.name}</span>
                              {b.media_type === "document" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                                  PDF
                                </span>
                              )}
                              {b.interactive_type === "button" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                                  CTA Buttons
                                </span>
                              )}
                              {b.interactive_type === "cta_url" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                  Link Button
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 line-clamp-1 max-w-[280px] mt-0.5">
                              {b.message_template}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3.5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                isRunning
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                  : b.status === "completed"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {isRunning && <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />}
                              <span className="capitalize">{b.status}</span>
                            </span>
                          </td>

                          {/* Progress */}
                          <td className="px-4 py-3.5 min-w-[150px]">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-semibold text-slate-800">
                                {sent} / {total}
                              </span>
                              <span className="text-slate-400 font-mono text-[11px]">{pct}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  pct === 100 ? "bg-blue-600" : "bg-emerald-500"
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>

                          {/* Delivered */}
                          <td className="px-4 py-3.5 text-right font-medium text-teal-800">
                            {b.delivered_count || sent}
                          </td>

                          {/* Read */}
                          <td className="px-4 py-3.5 text-right font-medium text-sky-700">
                            {b.read_count || 0}
                          </td>

                          {/* Pacing */}
                          <td className="px-4 py-3.5 text-xs text-slate-500">
                            {b.delay_seconds || 3}s / lead
                          </td>

                          {/* Created */}
                          <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                            {dateShort(b.created_at)}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openBroadcastDetails(b.id)}
                                className="h-7 text-xs rounded-lg px-2.5 text-slate-700 hover:text-emerald-700"
                                title="View Delivery Logs"
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                Logs
                              </Button>

                              {isRunning && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCancelBroadcast(b.id)}
                                  className="h-7 text-xs rounded-lg px-2 text-rose-600 border-rose-200 hover:bg-rose-50"
                                  title="Cancel Broadcast"
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </Button>
                              )}

                              {!isRunning && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteBroadcast(b.id)}
                                  className="h-7 text-xs rounded-lg px-2 text-slate-400 hover:text-rose-600"
                                  title="Delete Record"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── AI VOICE CALLING TAB CONTENT ── */}
        <TabsContent value="voice" className="space-y-5">
          <div className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-200">
            <span>
              Outbound AI dialer engine provider:&nbsp;
              <Badge
                variant="outline"
                className={
                  provider === "omnidim" || provider === "vapi"
                    ? "border-emerald-300 text-emerald-800 bg-emerald-50"
                    : "border-indigo-300 text-indigo-800"
                }
              >
                {provider === "omnidim" || provider === "vapi" ? "AI Voice Agent (Live)" : "MOCK"}
              </Badge>
            </span>
            <span className="text-slate-400">Autonomous voice calls with sentiment analysis & transcript sync</span>
          </div>

          <Card className="border-indigo-100 bg-white overflow-hidden rounded-2xl shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaigns-table">
                <thead className="bg-indigo-50/60 text-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Campaign</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Targets</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Connected</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Interested</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Appts</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Quotes</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Conv%</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-50/60">
                  {voiceLoading ? (
                    <tr>
                      <td colSpan={9} className="p-6 text-center text-slate-500 text-xs">
                        Loading voice campaigns…
                      </td>
                    </tr>
                  ) : voiceItems.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-10 text-center text-slate-500">
                        No voice campaigns yet. Click <b>New Voice Campaign</b> to launch an outbound AI caller.
                      </td>
                    </tr>
                  ) : (
                    voiceItems.map((c) => (
                      <tr key={c.id} className="border-t border-indigo-50 hover:bg-indigo-50/30">
                        <td className="px-4 py-3">
                          <Link
                            to={`/campaigns/${c.id}`}
                            data-testid={`campaign-row-${c.id}`}
                            className="font-medium text-slate-900 hover:text-indigo-800"
                          >
                            {c.name}
                          </Link>
                          <div className="text-xs text-slate-500">{c.description || "—"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle(c.status)}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{c.stats?.total_targets ?? 0}</td>
                        <td className="px-4 py-3 text-right text-emerald-800">{c.stats?.connected ?? 0}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.interested_leads ?? 0}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.appointment_bookings ?? 0}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.quotations_requested ?? 0}</td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {((c.stats?.conversion_rate ?? 0) * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{dateShort(c.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── BROADCAST RECIPIENT LOGS MODAL ── */}
      <Dialog open={!!viewBroadcastId} onOpenChange={(open) => !open && setViewBroadcastId(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-serif">
              <CheckCheck className="w-5 h-5 text-emerald-600" />
              <span>Broadcast Delivery Logs: {broadcastDetail?.name}</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Live delivery status for each recipient reached in this campaign
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-12 text-center text-xs text-slate-500 flex flex-col items-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-emerald-600" />
              <span>Loading delivery logs…</span>
            </div>
          ) : broadcastDetail ? (
            <div className="space-y-4 py-2">
              {/* Campaign summary strip */}
              <div className="grid grid-cols-4 gap-2 text-xs bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Status</span>
                  <span className="font-semibold capitalize text-slate-800">{broadcastDetail.status}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Recipients</span>
                  <span className="font-semibold text-slate-800">
                    {broadcastDetail.sent_count} / {broadcastDetail.total_recipients}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Delivered</span>
                  <span className="font-semibold text-teal-700">
                    {broadcastDetail.delivered_count || broadcastDetail.sent_count}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Read Ticks</span>
                  <span className="font-semibold text-sky-700">{broadcastDetail.read_count || 0}</span>
                </div>
              </div>

              {/* Recipient Rows Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Recipient</th>
                      <th className="text-left px-3 py-2 font-semibold">Phone</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                      <th className="text-left px-3 py-2 font-semibold">Time</th>
                      <th className="text-left px-3 py-2 font-semibold">Message ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(broadcastDetail.recipients || []).map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                        <td className="px-3 py-2 text-slate-600 font-mono">{r.phone}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              r.status === "read"
                                ? "bg-sky-100 text-sky-800"
                                : r.status === "delivered"
                                ? "bg-teal-100 text-teal-800"
                                : r.status === "sent"
                                ? "bg-emerald-100 text-emerald-800"
                                : r.status === "failed"
                                ? "bg-rose-100 text-rose-800"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">
                          {r.sent_at ? dateTime(r.sent_at) : "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-400 font-mono text-[10px] truncate max-w-[140px]">
                          {r.wa_message_id || r.error_message || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setViewBroadcastId(null)}
              className="text-xs rounded-xl"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── BROADCAST COMPOSER MODAL ── */}
      <WhatsAppBroadcastModal
        open={broadcastModalOpen}
        onOpenChange={setBroadcastModalOpen}
        onSuccess={loadBroadcasts}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Calling Sub-components (Preserved)
// ---------------------------------------------------------------------------

function CallingSettingsDialog({ onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/calling").then(r => setS(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);
  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/calling", s);
      toast.success("Calling settings updated");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }
  if (!s) return <DialogContent><div className="p-4">Loading…</div></DialogContent>;
  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="font-serif">Calling Settings</DialogTitle>
        <DialogDescription>Default pacing for all campaigns (per-campaign overrides allowed).</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Daily Call Limit" testid="settings-daily-limit"
          value={s.daily_call_limit}
          onChange={(v) => setS({ ...s, daily_call_limit: Number(v) || 0 })} />
        <NumberField label="Calls / Minute" testid="settings-cpm"
          value={s.calls_per_minute}
          onChange={(v) => setS({ ...s, calls_per_minute: Number(v) || 0 })} />
        <TimeField label="Start Time (IST)" testid="settings-start"
          value={s.start_time}
          onChange={(v) => setS({ ...s, start_time: v })} />
        <TimeField label="End Time (IST)" testid="settings-end"
          value={s.end_time}
          onChange={(v) => setS({ ...s, end_time: v })} />
      </div>
      <DialogFooter>
        <Button data-testid="settings-save-btn" disabled={saving} onClick={save}
                className="bg-indigo-700 hover:bg-indigo-800">
          Save Settings
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function NewCampaignDialog({ onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", campaign_prompt: "",
    source_type: "leads",
    status_filter: [], source_filter: [],
    daily_call_limit: null, calls_per_minute: null,
  });
  const [saving, setSaving] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  useEffect(() => {
    const spec = {
      status: form.status_filter.length ? form.status_filter : null,
      source: form.source_filter.length ? form.source_filter : null,
    };
    api.post("/campaigns/preview-leads", spec)
      .then(r => setPreviewCount(r.data.count))
      .catch(() => setPreviewCount(null));
  }, [form.status_filter, form.source_filter]);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        campaign_prompt: form.campaign_prompt || null,
        source_type: form.source_type,
        filters: {
          status: form.status_filter.length ? form.status_filter : null,
          source: form.source_filter.length ? form.source_filter : null,
        },
        daily_call_limit: form.daily_call_limit ? Number(form.daily_call_limit) : null,
        calls_per_minute: form.calls_per_minute ? Number(form.calls_per_minute) : null,
      };
      await api.post("/campaigns", payload);
      toast.success("Campaign created");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-serif">New Calling Campaign</DialogTitle>
        <DialogDescription>Create an outbound campaign targeted by lead filters.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Campaign Name *</Label>
          <Input data-testid="new-camp-name" value={form.name}
                 onChange={e => setForm({ ...form, name: e.target.value })}
                 placeholder="e.g. Diwali Web Redesign Wave 1" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Input data-testid="new-camp-desc" value={form.description}
                 onChange={e => setForm({ ...form, description: e.target.value })}
                 placeholder="Optional notes" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Target Lead Status</Label>
          <MultiSelect options={STATUSES} selected={form.status_filter}
                       onChange={v => setForm({ ...form, status_filter: v })} />
        </div>
        <div>
          <Label className="text-xs">Target Lead Source</Label>
          <MultiSelect options={SOURCES} selected={form.source_filter}
                       onChange={v => setForm({ ...form, source_filter: v })} />
        </div>
        <div className="text-xs text-slate-500 bg-indigo-50/40 p-2 rounded border border-indigo-100 flex items-center justify-between">
          <span>Matching leads with phone:</span>
          <span className="font-semibold text-indigo-900" data-testid="preview-count">
            {previewCount ?? "…"}
          </span>
        </div>
        <div>
          <Label className="text-xs">Voice Prompt / Script</Label>
          <Textarea data-testid="new-camp-prompt" rows={4} value={form.campaign_prompt}
                    onChange={e => setForm({ ...form, campaign_prompt: e.target.value })}
                    placeholder="Instructions for the AI voice caller..." className="mt-1" />
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="new-camp-save" disabled={saving || !form.name.trim()} onClick={save}
                className="bg-indigo-700 hover:bg-indigo-800">
          Create Campaign
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function MultiSelect({ options, selected, onChange }) {
  function toggle(o) {
    if (selected.includes(o)) onChange(selected.filter(x => x !== o));
    else onChange([...selected, o]);
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {options.map(o => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)}
                  className={`text-xs px-2 py-0.5 rounded border ${
                    on ? "bg-indigo-700 text-white border-indigo-700"
                       : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} type="number" value={value ?? ""}
             onChange={e => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}

function TimeField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} type="time" value={value ?? ""}
             onChange={e => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}
